import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import Loading from "../../components/Loading";
import Data from '../../../repository';

import styles from '../../../styles/Admin.module.scss';

function List() {
	const [loading, setLoading] = useState(true);
	const [services, setServices] = useState([]);
	const [search, setSearch] = useState("");
	const [importLoading, setImportLoading] = useState(false);
	const [importUploadProgress, setImportUploadProgress] = useState(0);
	const [importResult, setImportResult] = useState(null);
	const [importError, setImportError] = useState("");
	const fileInputRef = useRef(null);

	useEffect(() => {
		refreshServices();
	}, [])

	const refreshServices = async () => {
		setLoading(true);
		try {
			const res = await Data.fetchServices();
			const sorted = res.data.sort((a, b) => {
				if (a.isPublished !== b.isPublished) return b.isPublished - a.isPublished;
				return a.sortOrder - b.sortOrder;
			});
			setServices(sorted);
		} catch (error) {
			console.error('Error fetching services:', error);
		}
		setLoading(false);
	}

	const downloadBlob = (data, filename) => {
		const blob = new Blob([data], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', filename);
		document.body.appendChild(link);
		link.click();
		link.parentNode.removeChild(link);
		window.URL.revokeObjectURL(url);
	}

	const exportCsv = async () => {
		const response = await Data.exportServices();
		downloadBlob(response.data, 'services.csv');
	}

	const downloadTemplate = async () => {
		const response = await Data.exportServiceImportTemplate();
		downloadBlob(response.data, 'services-import-template.csv');
	}

	const importCsv = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setImportLoading(true);
		setImportUploadProgress(0);
		setImportResult(null);
		setImportError("");

		try {
			let finalResult = null;
			const response = await Data.importServices(file, (progressEvent) => {
				if (!progressEvent.total) return;
				setImportUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
			}, (progress) => {
				finalResult = progress;
				setImportResult(progress);
			});
			if (!finalResult && response.data) {
				const lines = String(response.data).trim().split('\n').filter(Boolean);
				if (lines.length) setImportResult(JSON.parse(lines[lines.length - 1]));
			}
			await refreshServices();
		} catch (error) {
			setImportError(error.response?.data?.message || "Service import failed.");
		} finally {
			setImportLoading(false);
			event.target.value = "";
		}
	}

	const downloadFailedRecords = () => {
		if (!importResult?.failedRecords?.length) return;
		const headers = ['row', 'name', 'message'];
		const escape = (value) => {
			if (value === null || value === undefined) return '';
			const text = String(value);
			return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
		};
		const lines = importResult.failedRecords.map((failed) => headers.map((header) => escape(failed[header])).join(','));
		downloadBlob([headers.join(','), ...lines].join('\n'), 'failed-service-import-records.csv');
	}

	const formatPrice = (price) => {
		if (price === 0) return 'Free';
		return `£${(price / 100).toFixed(2)}`;
	};

	if (loading) return <Loading button={false} />

	const filteredServices = services.filter((service) => {
		return service.name.toLowerCase().includes(search.toLowerCase());
	});

	return (
		<div className={styles.list}>
			<input
				type="text"
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				placeholder="Search services..."
			/>

			<button onClick={exportCsv} className={styles.button}>Export Services</button>
			<button onClick={downloadTemplate} className={styles.button}>Download Sample Sheet</button>
			<button onClick={() => fileInputRef.current?.click()} disabled={importLoading} className={styles.button}>
				{importLoading ? 'Importing Services...' : 'Import Services'}
			</button>
			<input
				ref={fileInputRef}
				type="file"
				accept=".csv,text/csv"
				onChange={importCsv}
				disabled={importLoading}
				style={{ display: 'none' }}
			/>

			{importLoading && (
				<div>
					<p>Upload progress: {importUploadProgress}%</p>
					<progress value={importUploadProgress} max="100" style={{ width: '100%' }} />
					{importResult ? (
						<p>Stored: {importResult.stored} / {importResult.total} | Left: {importResult.remaining}</p>
					) : (
						<p>Processing starts after the CSV upload completes.</p>
					)}
				</div>
			)}

			{importError && <p>{importError}</p>}

			{importResult && (
				<div>
					<p>Stored: {importResult.stored} / {importResult.total} | Skipped: {importResult.skipped}</p>
					{!!importResult.failedRecords?.length && (
						<button onClick={downloadFailedRecords} className={styles.button}>Download Failed Records</button>
					)}
				</div>
			)}

			{!filteredServices.length && !search && (
				<div>
					<p>There aren't any services yet.</p>
					<Link to="create" className="btn btnPrimary" style={{ marginTop: '16px', display: 'inline-block' }}>
						Create your first service
					</Link>
				</div>
			)}

			{!filteredServices.length && search && (
				<div><p>No services found matching "{search}"</p></div>
			)}

			{filteredServices.map((service) => (
				<Link key={service._id} to={service._id} className={`${styles.service} ${!service.isPublished ? styles.disabled : ''}`}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
						<div style={{
							width: '48px',
							height: '48px',
							borderRadius: '8px',
							border: '1px solid #e7e7e7',
							overflow: 'hidden',
							background: '#f5f5f5',
							flexShrink: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontWeight: 600,
							color: '#666'
						}}>
							{service.image ? (
								<img src={service.image} alt={service.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
							) : (
								<span>{service.name?.charAt(0)?.toUpperCase() || 'S'}</span>
							)}
						</div>
						<div>
							<p><strong>{service.name}</strong></p>
							<p style={{ fontSize: '14px', opacity: 0.6 }}>
								{service.duration} mins • {formatPrice(service.price)}
								{service.category && ` • ${service.category}`}
							</p>
						</div>
					</div>
					<p style={{
						padding: '4px 12px',
						borderRadius: '20px',
						fontSize: '12px',
						background: service.isPublished ? '#E8F5E9' : '#eee',
						color: service.isPublished ? '#2E7D32' : '#666'
					}}>
						{service.isPublished ? 'Published' : 'Draft'}
					</p>
				</Link>
			))}
		</div>
	)
}

export default List;
